import * as Sentry from '@sentry/browser'
import { QRCodeService } from 'features/QRCode'
import { stringEnumToArray } from 'helpers/common'
import { DATE_UNIT, ProductForForm, WARRANTY_TYPE } from 'types'
import * as yup from 'yup'
import { QR_CODE_LENGTH } from './constants'

type Context = yup.TestContext & { type?: string }

export const initialForm: ProductForForm = {
  currentState: 'serialNumber',
  isActive: true,
  skuId: '',
  code: '',
  name: '',
  model: '',
  brand: '',
  detail: '',
  currency: 'THB',
  price: '',
  serialNumber: '',
  dateOfPurchase: new Date(),
  // warranty section
  warrantyType: WARRANTY_TYPE.PERIOD,
  warrantyPeriod: '',
  warrantyUnit: DATE_UNIT.YEAR,
  startWarranty: null,
  endWarranty: null,
  maintenancePeriod: '',
  maintenanceUnit: DATE_UNIT.YEAR,
  qrCode: '',
  //branch
  imageProduct: '',
}

export const schemaForm = yup.object().shape({
  isActive: yup.boolean(),
  skuId: yup.string(),
  name: yup.string().required('product:name.required'),
  brand: yup.string(),
  detail: yup.string().nullable(),
  currency: yup.string().max(3).optional(),
  price: yup.string().max(1000000000).optional(),
  serialNumber: yup.string().nullable().max(50).optional(),
  dateOfPurchase: yup.date().required('product:dateOfPurchase.required'),
  // warranty section
  warrantyType: yup.mixed().oneOf(stringEnumToArray(WARRANTY_TYPE)),
  warrantyPeriod: yup
    .string()
    .max(2)
    .optional()
    .when('warrantyType', {
      is: WARRANTY_TYPE.PERIOD,
      then: yup.string().required('product:warranty.period.required'),
    }),
  warrantyUnit: yup
    .mixed()
    .oneOf(stringEnumToArray(DATE_UNIT))
    .optional()
    .when('warrantyType', {
      is: WARRANTY_TYPE.PERIOD,
      then: yup.mixed().required('product:warranty.unit.required'),
    }),
  startWarranty: yup
    .date()
    .nullable()
    .optional()
    .when('warrantyType', {
      is: WARRANTY_TYPE.PERIOD,
      then: yup
        .date()
        .required('product:warranty.startDate.required')
        .nullable(),
    }),
  endWarranty: yup
    .date()
    .nullable()
    .optional()
    .when('warrantyType', {
      is: WARRANTY_TYPE.PERIOD,
      then: yup.date().required('product:warranty.endDate.required').nullable(),
    }),
  maintenancePeriod: yup
    .string()
    .max(2)
    .optional()
    .when('warrantyType', {
      is: (value: string) =>
        value === WARRANTY_TYPE.PERIOD || value === WARRANTY_TYPE.LIFETIME,
      then: yup
        .string()
        .required('product:warranty.maintenancePeriod.required'),
    }),
  maintenanceUnit: yup
    .mixed()
    .oneOf(stringEnumToArray(DATE_UNIT))
    .optional()
    .when('warrantyType', {
      is: (value: string) =>
        value === WARRANTY_TYPE.PERIOD || value === WARRANTY_TYPE.LIFETIME,
      then: yup
        .string()
        .required('product:warranty.maintenanceUnit.required')
        .nullable(),
    }),
  qrCode: yup
    .string()
    .optional()
    .test('qrCode', async (qrCode, context: Context) => {
      const { path, createError, resolve, parent, type } = context
      if (!qrCode) {
        return resolve(true)
      }

      if (qrCode.length !== QR_CODE_LENGTH) {
        return createError({
          path,
          message: 'product:warranty.mapping.invalid',
        })
      }

      if (
        parent?.currentState === type ||
        parent?.currentState === 'onSubmit'
      ) {
        try {
          const response = await QRCodeService.findOneById(qrCode)

          if (response.isAvailable) {
            return resolve(true)
          }

          const pathname = window.location.pathname
          const matchedProductId = pathname.match(
            new RegExp(/product\/(.*?)\/edit/),
          )
          const productId = matchedProductId && matchedProductId[1]

          if (productId === response.productId) {
            return resolve(true)
          }

          return createError({
            path,
            message: 'product:warranty.mapping.duplicate',
          })
        } catch (error) {
          Sentry.captureException(error)

          return createError({
            path,
            message: 'product:warranty.mapping.invalid',
          })
        }
      }

      return false
    }),
})
